import React, { useState, useEffect } from "react";
import { Layout, Menu, Button, Tooltip, Switch, Space, Avatar, Dropdown } from "antd";
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SettingOutlined,
  UpOutlined,
  DownOutlined,
  DashboardOutlined,
  FileTextOutlined,
  TeamOutlined,
  UserOutlined,
  FileOutlined,
  BookOutlined,
  ShoppingCartOutlined,
  DollarOutlined,
  CalendarOutlined,
  MessageOutlined,
  LogoutOutlined,
  EditOutlined,
  HomeOutlined,
  FileImageOutlined,
  ApiOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../providers/authProvider";

const { Sider } = Layout;

interface INavigationSettings {
  collapsed: boolean;
  position: "left" | "top";
  theme: "light" | "dark";
}

interface MenuItem {
  key: string;
  label: string;
  icon?: React.ReactNode;
  route?: string;
  children?: MenuItem[];
}

interface CustomSiderProps {
  collapsed?: boolean;
  onCollapse?: (collapsed: boolean) => void;
}

export const CustomSider: React.FC<CustomSiderProps> = ({ 
  collapsed: propCollapsed, 
  onCollapse 
}) => {
  const location = useLocation();
  const { user, logout } = useAuth();

  // Logo-Komponente
  const BuchungsmanagerLogo: React.FC<{ collapsed: boolean; theme: "light" | "dark" }> = ({ collapsed, theme }) => {
    const logoStyle: React.CSSProperties = {
      display: "flex",
      alignItems: "center",
      justifyContent: collapsed ? "center" : "flex-start",
      gap: collapsed ? 0 : 8,
      textDecoration: "none",
      height: collapsed ? "32px" : "40px",
      overflow: "hidden",
    };

    const logoImageStyle: React.CSSProperties = {
      height: collapsed ? "24px" : "32px",
      width: "auto",
      filter: theme === "dark" ? "brightness(0) invert(1)" : "none",
    };

    const textStyle: React.CSSProperties = {
      color: theme === "dark" ? "#fff" : "#1890ff",
      fontWeight: "bold",
      fontSize: "16px",
      whiteSpace: "nowrap",
    };

    return (
      <Link to="/dashboard" style={logoStyle}>
        <img 
          src="/fileadmin/logo-no-bg.svg" 
          alt="Buchungsmanager" 
          style={logoImageStyle}
        />
      </Link>
    );
  };

  // User-Menü für Profil und Logout
  const UserMenu = ({ collapsed, theme }: { collapsed: boolean; theme: "light" | "dark" }) => {
    const handleLogout = async () => {
      try {
        await logout();
        window.location.reload();
      } catch (error) {
        console.error('Logout-Fehler:', error);
      }
    };

    const menuItems = [
      {
        key: 'profile',
        icon: <EditOutlined />,
        label: 'Profil bearbeiten',
        onClick: () => {
          // TODO: Implementiere Profil-Dialog oder Navigate zu Profil-Seite
          console.log('Profil bearbeiten - noch nicht implementiert');
        }
      },
      {
        type: 'divider' as const,
      },
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: 'Abmelden',
        onClick: handleLogout,
        danger: true,
      },
    ];

    if (collapsed) {
      return (
        <Space direction="vertical" style={{ width: "100%" }}>
          <Tooltip title="Profil bearbeiten" placement="right">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => console.log('Profil bearbeiten - noch nicht implementiert')}
              style={{
                color: theme === 'dark' ? '#fff' : '#000',
                width: "100%"
              }}
            />
          </Tooltip>
          <Tooltip title="Abmelden" placement="right">
            <Button
              type="text"
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              style={{
                color: theme === 'dark' ? '#fff' : '#000',
                width: "100%"
              }}
            />
          </Tooltip>
        </Space>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{
          padding: '8px 12px',
          borderRadius: 6,
          background: theme === 'dark' ? '#1f1f1f' : '#f5f5f5',
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }}>
          <Avatar icon={<UserOutlined />} size="small" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              color: theme === 'dark' ? '#fff' : '#000',
              fontSize: '12px',
              fontWeight: 'bold',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {user?.firstName} {user?.lastName}
            </div>
            <div style={{
              color: theme === 'dark' ? '#bfbfbf' : '#666',
              fontSize: '10px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {user?.email}
            </div>
          </div>
        </div>
        
        <Space direction="vertical" style={{ width: "100%" }}>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => console.log('Profil bearbeiten - noch nicht implementiert')}
            style={{
              color: theme === 'dark' ? '#fff' : '#000',
              width: "100%",
              textAlign: "left",
              justifyContent: "flex-start"
            }}
          >
            Profil bearbeiten
          </Button>
          <Button
            type="text"
            icon={<LogoutOutlined />}
            onClick={handleLogout}
            style={{
              color: theme === 'dark' ? '#fff' : '#000',
              width: "100%",
              textAlign: "left",
              justifyContent: "flex-start"
            }}
          >
            Abmelden
          </Button>
        </Space>
      </div>
    );
  };

  // Statische Menü-Definition mit direkten Routes
  const menuItems: MenuItem[] = [
    // === HAUPTBEREICHE ===
    {
      key: 'dashboard',
      label: 'Dashboard',
      icon: <DashboardOutlined />,
      route: '/dashboard'
    },
    {
      key: 'bookings',
      label: 'Buchungskalender',
      icon: <CalendarOutlined />,
      route: '/buchungen'
    },
    {
      key: 'guests',
      label: 'Gästedaten',
      icon: <TeamOutlined />,
      route: '/gaeste'
    },
    {
      key: 'communication',
      label: 'Gästekommunikation',
      icon: <MessageOutlined />,
      children: [
        {
          key: 'guest-communication',
          label: 'Kommunikation',
          route: '/gaestekommunikation'
        },
        {
          key: 'templates',
          label: 'Textbausteine',
          route: '/textbausteine'
        }
      ]
    },
    {
      key: 'invoices',
      label: 'Rechnungen & Angebote',
      icon: <FileTextOutlined />,
      route: '/rechnungen'
    },

    // === TRENNER 1 ===
    {
      key: 'divider-1',
      label: '───────────────────',
      icon: null,
      route: undefined
    },

    // === VERWALTUNG ===
    {
      key: 'senders',
      label: 'Objekte & Inserate',
      icon: <HomeOutlined />,
      route: '/objekte'
    },
    {
      key: 'arrival-departure',
      label: 'An- & Abreise',
      icon: <CalendarOutlined />,
      route: '/an-abreise'
    },
    {
      key: 'pricing',
      label: 'Preise',
      icon: <DollarOutlined />,
      route: '/preis-einstellungen'
    },
    {
      key: 'channelmanager',
      label: 'Channel Manager',
      icon: <ApiOutlined />,
      route: '/channelmanager'
    },

    // === TRENNER 2 ===
    {
      key: 'divider-2',
      label: '___________________',
      icon: null,
      route: undefined
    },

    // === TOOLS & SERVICES ===
    {
      key: 'tasklists',
      label: 'Aufgabenlisten',
      icon: <UnorderedListOutlined />,
      route: '/aufgabenlisten'
    },
    {
      key: 'digital-guestmap',
      label: 'Digitale Gästemappe',
      icon: <BookOutlined />,
      children: [
        {
          key: 'digital-guestmap-editor',
          label: 'Gästemappe Editor',
          route: '/digitale-gaestemappe'
        },
        {
          key: 'active-guestmaps',
          label: 'Aktive Gästemappen',
          route: '/aktive-gaestemappen'
        },
        {
          key: 'guestmap-templates',
          label: 'Gästemappen-Vorlagen',
          route: '/gaestemappen-vorlagen'
        }
      ]
    },
    {
      key: 'guided-booking',
      label: 'Guided OnePage Booking',
      icon: <ShoppingCartOutlined />,
      route: '/guided-onepage-booking'
    },
    {
      key: 'website-widget',
      label: 'Webseite & Widget',
      icon: <EditOutlined />,
      route: '/webseite-widget'
    },
    {
      key: 'media-manager',
      label: 'Medien & KI-Bildgenerator',
      icon: <FileImageOutlined />,
      route: '/medien-bilder'
    }
  ];

  // Navigation-Einstellungen aus LocalStorage laden
  const [settings, setSettings] = useState<INavigationSettings>(() => {
    const saved = localStorage.getItem('navigation-settings');
    return saved ? JSON.parse(saved) : {
      collapsed: false,
      position: 'left' as const,
      theme: 'light' as const
    };
  });

  const [showSettings, setShowSettings] = useState(false);
  const [internalCollapsed, setInternalCollapsed] = useState(settings.collapsed);

  // Verwende entweder prop oder interne collapsed state
  const collapsed = propCollapsed !== undefined ? propCollapsed : internalCollapsed;

  useEffect(() => {
    // Speichere Einstellungen im LocalStorage
    localStorage.setItem('navigation-settings', JSON.stringify(settings));
  }, [settings]);

  const handleCollapse = (value: boolean) => {
    setInternalCollapsed(value);
    setSettings(prev => ({ ...prev, collapsed: value }));
    onCollapse?.(value);
  };

  const updateSettings = (key: keyof INavigationSettings, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  // Generiere Menü-Items für Ant Design
  const generateMenuItems = () => {
    return menuItems.map((item) => {
      // Behandle Trenner separat
      if (item.key.startsWith('divider-')) {
        return {
          key: item.key,
          label: (
            <div style={{ 
              textAlign: 'center', 
              color: '#d9d9d9', 
              fontSize: '12px',
              fontWeight: 'normal',
              cursor: 'default',
              userSelect: 'none',
              padding: '8px 0',
              margin: '4px 0',
              borderBottom: '1px solid #f0f0f0',
              whiteSpace: 'nowrap',
              overflow: 'hidden'
            }}>
              {collapsed ? '─' : item.label}
            </div>
          ),
          disabled: true,
        };
      }
      
      if (item.children) {
        return {
          key: item.key,
          icon: item.icon,
          label: item.label,
          children: item.children.map((child) => ({
            key: child.key,
            label: (
              <Link to={child.route || '/'} style={{ textDecoration: 'none' }}>
                {child.label}
              </Link>
            ),
          })),
        };
      } else {
        return {
          key: item.key,
          icon: item.icon,
          label: (
            <Link to={item.route || '/'} style={{ textDecoration: 'none' }}>
              {item.label}
            </Link>
          ),
        };
      }
    });
  };

  // Aktuelle Route ermitteln für selectedKeys
  const getSelectedKeys = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    if (pathSegments.length === 0) return ['dashboard'];
    
    // Route-Mapping für bessere Navigation
    const routeMapping: { [key: string]: string } = {
      'dashboard': 'dashboard',
      'buchungen': 'bookings',
      'gaeste': 'guests',
      'gaestekommunikation': 'guest-communication',
      'rechnungen': 'invoices',
      'objekte': 'senders',
      'an-abreise': 'arrival-departure',
      'preis-einstellungen': 'pricing',
      'channelmanager': 'channelmanager',
      'aufgabenlisten': 'tasklists',
      'digitale-gaestemappe': 'digital-guestmap',
      'aktive-gaestemappen': 'active-guestmaps',
      'gaestemappen-vorlagen': 'guestmap-templates',
      'guided-onepage-booking': 'guided-booking',
      'webseite-widget': 'website-widget',
      'medien-bilder': 'media-manager',
      'textbausteine': 'templates',
    };

    const firstSegment = pathSegments[0];
    return [routeMapping[firstSegment] || firstSegment];
  };

  // Horizontale Navigation (oben)
  const isTopPosition = settings.position === 'top';
  if (isTopPosition) {
    return (
      <div style={{ 
        background: '#fff', 
        padding: '0 24px',
        borderBottom: '1px solid #f0f0f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Menu
          mode="horizontal"
          selectedKeys={getSelectedKeys()}
          items={generateMenuItems()}
          style={{ border: 'none', minWidth: 0, flex: 1 }}
        />
        
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Tooltip title="Navigation Einstellungen">
            <Button
              type="text"
              icon={<SettingOutlined />}
              onClick={() => setShowSettings(!showSettings)}
            />
          </Tooltip>
        </div>

        {/* Einstellungs-Dropdown für horizontale Navigation */}
        {showSettings && (
          <div style={{
            position: 'absolute',
            top: '100%',
            right: 24,
            background: '#fff',
            padding: 16,
            border: '1px solid #d9d9d9',
            borderRadius: 6,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            zIndex: 1000,
            minWidth: 200
          }}>
            <div style={{ marginBottom: 12 }}>
              <label>Position:</label>
              <div style={{ marginTop: 4 }}>
                <Switch
                  checked={settings.position === 'left'}
                  onChange={(checked) => updateSettings('position', checked ? 'left' : 'top')}
                  checkedChildren="Links"
                  unCheckedChildren="Oben"
                />
              </div>
            </div>
            <div>
              <label>Theme:</label>
              <div style={{ marginTop: 4 }}>
                <Switch
                  checked={settings.theme === 'dark'}
                  onChange={(checked) => updateSettings('theme', checked ? 'dark' : 'light')}
                  checkedChildren="Dunkel"
                  unCheckedChildren="Hell"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Vertikale Navigation (links)
  return (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={handleCollapse}
      trigger={null}
      theme={settings.theme}
      width={256}
      collapsedWidth={80}
      style={{
        position: 'relative',
        minHeight: '100vh',
      }}
    >
      {/* Header mit Logo und Collapse-Button */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px',
        borderBottom: '1px solid #f0f0f0'
      }}>
        <BuchungsmanagerLogo collapsed={collapsed} theme={settings.theme} />
        <Button
          type="text"
          icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          onClick={() => handleCollapse(!collapsed)}
          style={{
            color: settings.theme === 'dark' ? '#fff' : '#000'
          }}
        />
      </div>

      {/* Hauptmenü */}
      <Menu
        mode="inline"
        selectedKeys={getSelectedKeys()}
        items={generateMenuItems()}
        theme={settings.theme}
        inlineCollapsed={collapsed}
        style={{ 
          borderRight: 0, 
          height: 'calc(100vh - 220px)', 
          overflowY: 'auto' 
        }}
      />

      {/* User-Menü und Einstellungen am unteren Rand */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        borderTop: '1px solid #f0f0f0',
        padding: '8px'
      }}>
        {/* User-Bereich */}
        <div style={{ marginBottom: 12 }}>
          <UserMenu collapsed={collapsed} theme={settings.theme} />
        </div>

        {/* Einstellungen-Bereich */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Tooltip title="Navigation Einstellungen">
            <Button
              type="text"
              icon={<SettingOutlined />}
              onClick={() => setShowSettings(!showSettings)}
              style={{
                color: settings.theme === 'dark' ? '#fff' : '#000'
              }}
            />
          </Tooltip>
          {!collapsed && (
            <Button
              type="text"
              icon={showSettings ? <UpOutlined /> : <DownOutlined />}
              onClick={() => setShowSettings(!showSettings)}
              style={{
                color: settings.theme === 'dark' ? '#fff' : '#000'
              }}
            />
          )}
        </div>

        {/* Einstellungs-Panel für vertikale Navigation */}
        {showSettings && !collapsed && (
          <div style={{ 
            padding: '12px 8px',
            background: settings.theme === 'dark' ? '#141414' : '#fafafa',
            borderRadius: 4,
            marginTop: 8
          }}>
            <div style={{ marginBottom: 12 }}>
              <label style={{ 
                color: settings.theme === 'dark' ? '#fff' : '#000',
                fontSize: '12px'
              }}>
                Position:
              </label>
              <div style={{ marginTop: 4 }}>
                <Switch
                  size="small"
                  checked={settings.position === 'left'}
                  onChange={(checked) => updateSettings('position', checked ? 'left' : 'top')}
                  checkedChildren="Links"
                  unCheckedChildren="Oben"
                />
              </div>
            </div>
            <div>
              <label style={{ 
                color: settings.theme === 'dark' ? '#fff' : '#000',
                fontSize: '12px'
              }}>
                Theme:
              </label>
              <div style={{ marginTop: 4 }}>
                <Switch
                  size="small"
                  checked={settings.theme === 'dark'}
                  onChange={(checked) => updateSettings('theme', checked ? 'dark' : 'light')}
                  checkedChildren="Dunkel"
                  unCheckedChildren="Hell"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </Sider>
  );
}; 